'use client'

import { Suspense, type ComponentProps } from 'react'

import { usePathname } from 'next/navigation'

import {
  isOutboundAffiliateUrl,
  tagOutboundAffiliateUrl,
  type OutboundAffiliateContext,
} from '@/lib/analytics/outbound-affiliate'
import { documentPath } from '@/lib/seo/document-path'
import { linkRel, requiresDocumentUrl } from '@/lib/seo/indexing-policy'

import { useIndexingPolicy } from './indexing-provider'

type Props = ComponentProps<'a'> & {
  affiliateContext?: OutboundAffiliateContext
  'data-tracking'?: string
  'data-event-name'?: string
  'data-event-position'?: string
}

const ResolvedAnchor = ({ href, rel, documentUrl, ...props }: Props & { documentUrl?: string }) => {
  const policy = useIndexingPolicy()
  return <a {...props} href={href} rel={linkRel(href, rel, policy, documentUrl, props.target)} />
}

const RelativeAnchor = (props: Props) => <ResolvedAnchor {...props} documentUrl={documentPath(usePathname())} />

export const Anchor = ({ affiliateContext, ...authored }: Props) => {
  const existingAffiliate = ['AffiliateAction', 'table-affiliate-visit'].includes(authored['data-event-name'] ?? '')
  const track = authored['data-tracking'] !== 'ignore' && !existingAffiliate && isOutboundAffiliateUrl(authored.href)
  const position =
    authored['data-event-position'] ?? affiliateContext?.position ?? authored['data-event-name'] ?? 'inline-link'
  const props = track
    ? {
        ...authored,
        href: affiliateContext
          ? tagOutboundAffiliateUrl(authored.href!, { ...affiliateContext, position })
          : authored.href,
        'data-track': true,
        'data-affiliate-reference': true,
        'data-event-name': authored['data-event-name'] ?? 'AffiliateAction',
        'data-event-position': position,
      }
    : authored
  if (requiresDocumentUrl(props.href)) {
    return (
      <Suspense fallback={<ResolvedAnchor {...props} />}>
        <RelativeAnchor {...props} />
      </Suspense>
    )
  }
  return <ResolvedAnchor {...props} />
}
